// TerrainShadowDepthMaterial — Set15 P8 (§6.2): the SHADOW-CASTING depth material for 3D terrain.
//
// Three's shadow pass renders each `castShadow` object with a depth material (the shadow map is
// an RGBA-depth-packed depth buffer). The terrain's displacement is done in the VERTEX shader
// (`MATERIAL_VERTEX_GLSL`): `transformed.z += h * u_displaceScale` from the per-chunk D-less
// `u_geoHeightGeom` lattice. A default `MeshDepthMaterial` has no such displacement, so a casting
// chunk would cast from its FLAT z=0 geometry — the shadow would fall from a plane, not from the
// mountains.
//
// So the casting chunk uses a CUSTOM depth material (assigned as `mesh.customDepthMaterial`) whose
// vertex shader runs the SAME displacement. We use a `ShaderMaterial` (NOT `MeshDepthMaterial`)
// for a crucial reason: built-in materials like `MeshDepthMaterial` do NOT carry a real `uniforms`
// object, and `Material.clone()` does NOT copy one — so the per-chunk clone has `uniforms ===
// undefined`, and setting the per-chunk `u_geoHeightGeom` throws. A `ShaderMaterial` HAS a native
// `uniforms` object that `.clone()` deep-copies, so per-chunk lattice binding is safe.
//
// Three's `getDepthMaterial` accepts ANY material as `object.customDepthMaterial` and the shadow
// pass renders it with the light's shadow camera. The fragment writes three's `packDepthToRGBA(
// gl_FragCoord.z)` depth packing (the `RGBA` depth-packing format three's `getShadow()` /
// our `unpackRGBAToDepth` expect). The shadow map depth is stored LINEAR (the shadow camera's
// orthographic near/far), so the fragment just packs gl_FragCoord.z directly.
//
// Each chunk gets a CLONE of a shared template (three caches the GL program on the shader source;
// the clones share it), with its own `u_geoHeightGeom` + the `u_displaceScale` gate. The renderer
// assigns it as `mesh.customDepthMaterial` on every 3D casting chunk and updates the per-chunk
// lattice alongside the visible material's.
import * as THREE from "three";

/** The shadow-depth VERTEX shader — displacement-identical to `MATERIAL_VERTEX_GLSL`'s D-less
 *  fetch + `u_displaceScale` gate (D4/D6), minus the edge-stitch (the bias handles cast parity).
 *  `uv` is the standard PlaneGeometry/33×33-grid attribute. GLSL3 (three prepends `#version`). */
const TERRAIN_DEPTH_VERTEX_GLSL = /* glsl */`
// The worker's D-less height lattice (TerrainQuadtree.CHUNK_N = 33). The VERTEX displacement
// samples this (round, byte-identical to the fragment's matGeoHeight / the visible
// MATERIAL_VERTEX_GLSL displacement).
#define SHADOW_CHUNK_N 33
uniform float u_displaceScale;     // 0 in 2D (flat), 1 in 3D (displaced) — D4
uniform sampler2D u_geoHeightGeom; // D-less height lattice (R32F) — the §3.4 displacement field
out vec2 vShadowUv;
void main() {
	vShadowUv = uv;
	#include <begin_vertex>        // vec3 transformed = vec3(position);
	// Displace by the D-less lattice, gated by u_displaceScale (same as the visible material).
	// Round mapping: vertex (i,j) has uv == (i/32, j/32) → ivec2(round(uv*32)).
	ivec2 lat = ivec2(round(uv * float(SHADOW_CHUNK_N - 1)));
	float h = texelFetch(u_geoHeightGeom, lat, 0).r;
	transformed.z += h * u_displaceScale;
	#include <project_vertex>
}
`;

/** The shadow-depth FRAGMENT shader — three's `RGBA` depth packing (`packDepthToRGBA`), the
 *  format `getShadowMask`'s `unpackRGBAToDepth` expects. Ortho shadow camera ⇒ depth is linear. */
const TERRAIN_DEPTH_FRAGMENT_GLSL = /* glsl */`
#include <packing>
in vec2 vShadowUv;
out vec4 fragColor;
void main() {
	// Linear depth (ortho shadow camera) → three's RGBA depth packing.
	fragColor = packDepthToRGBA(gl_FragCoord.z);
}
`;

/** Build the SHARED shadow-casting depth material template. A `ShaderMaterial` (NOT
 *  `MeshDepthMaterial` — its uniforms must survive `.clone()` for per-chunk lattices). Each chunk
 *  gets a `clone()` with its own `u_geoHeightGeom` + the `u_displaceScale` gate. GLSL3. */
export function createTerrainDepthMaterial(): THREE.ShaderMaterial {
	return new THREE.ShaderMaterial({
		glslVersion: THREE.GLSL3,
		vertexShader: TERRAIN_DEPTH_VERTEX_GLSL,
		fragmentShader: TERRAIN_DEPTH_FRAGMENT_GLSL,
		uniforms: {
			u_displaceScale: {value: 0},
			u_geoHeightGeom: {value: null},
		},
		// The shadow pass renders from the light's perspective; the far side of a valley may face
		// away from the light, so cast from both sides (matches the visible terrain's DoubleSide).
		side: THREE.DoubleSide,
	});
}

/** Set the per-chunk uniforms on a depth-material clone (the D-less lattice + the 3D gate). */
export function setTerrainDepthUniforms(mat: THREE.ShaderMaterial, tex: THREE.DataTexture, displaceScale: number): void {
	if (mat.uniforms.u_geoHeightGeom) (mat.uniforms.u_geoHeightGeom.value as THREE.Texture) = tex;
	if (mat.uniforms.u_displaceScale) (mat.uniforms.u_displaceScale.value as number) = displaceScale;
}
